import type { StaffProfessionResponse, YearPlanResponse } from '../../../api/generated';
import {
  COMPETENCE_CAPACITY_WARNING_MARGIN_HOURS,
  HARDCODED_WEEKLY_COMPETENCE_CAPACITY_HOURS
} from '../../../constants/capacityConstants';
import { calculateUsedCompetenceHours } from '../../../services/capacityService';
import { getMockWeekKpi } from './mockWeekKpis';
import type { BedResource, ProfessionHours, WeekCalculationState, WeekKpi } from './types';

const OVERBOOKED_DEMO_BED_COUNT = 23;

// The chip layout renders a short code; the BE only provides a name, so the
// code is derived from its leading characters (e.g. "Fysioterapeut" → "Fys").
const CODE_LENGTH = 3;

const computePatientsCount = (yearPlan: YearPlanResponse, weekNumber: number): number => {
  let count = 0;
  for (const plan of yearPlan.treatmentPlans) {
    const week = plan.weeks.find((w) => w.weekNumber === weekNumber);
    if (!week) continue;
    const hasInterventions = (week.groups ?? []).some(
      (g) => (g.interventions ?? []).length > 0
    );
    if (!hasInterventions) continue;
    for (const group of week.groups ?? []) {
      count += (group.assignedPatients ?? []).filter((p) => !p.shadow).length;
    }
  }
  return count;
};

// Used hours are real (REH-202); the capacity limit stays hardcoded until the
// BE exposes staff working schedules (see capacityConstants).
const computeProfessionHours = (
  yearPlan: YearPlanResponse,
  weekNumber: number,
  kpiProfessions: readonly StaffProfessionResponse[]
): readonly ProfessionHours[] => {
  const usedHours = calculateUsedCompetenceHours(yearPlan, weekNumber);
  return kpiProfessions.map((profession) => ({
    profession: {
      id: profession.id,
      code: profession.name.slice(0, CODE_LENGTH),
      name: profession.name
    },
    planned: usedHours.get(profession.id) ?? 0,
    threshold: {
      limit: HARDCODED_WEEKLY_COMPETENCE_CAPACITY_HOURS,
      warningMargin: COMPETENCE_CAPACITY_WARNING_MARGIN_HOURS
    }
  }));
};

// In reality bedsCount ≤ patientsCount (some patients are outpatients). For
// the demo we approximate it as patientsCount, except on weeks with seeded
// overbookings where a hardcoded value keeps the red bed chip's number
// consistent with the multi-resource overbooking popover.
// TODO: replace with real bed-allocation data once BE exposes it.
const mockBedsCount = (patientsCount: number, bedResources: readonly BedResource[]): number =>
  bedResources.length > 0 ? OVERBOOKED_DEMO_BED_COUNT : patientsCount;

export const computeWeekKpi = (
  yearPlan: YearPlanResponse,
  weekNumber: number,
  staffProfessions: readonly StaffProfessionResponse[],
  calculation: WeekCalculationState
): WeekKpi => {
  const kpiProfessions = staffProfessions.filter((p) => p.showInKpi);
  const mock = getMockWeekKpi(weekNumber);
  const patientsCount = computePatientsCount(yearPlan, weekNumber);
  return {
    ...mock,
    weekNumber,
    calculation,
    // Unplanned interventions are an output of a solve — the solver reporting what it could not
    // place. While the count is still mocked, only show it on a week that has actually been
    // solved, so the header never claims 20 unplaced interventions on a week nothing has touched
    // (which would also light up the red WeekWarningIcon via aggregateWeekSeverity).
    planningIssues: calculation.status === 'CALCULATED' ? mock.planningIssues : [],
    professions: computeProfessionHours(yearPlan, weekNumber, kpiProfessions),
    patientsCount,
    bedsCount: mockBedsCount(patientsCount, mock.bedResources),
  };
};
